// Frontend permissions service - mirrors backend logic for domain-based authorization

export type Environment = 'DEV' | 'TEST' | 'PROD';
export type Operation = 'view' | 'create' | 'update' | 'delete' | 'sync';
export type Domain = 'RFV' | 'SchEng' | 'Global';
export type AccessLevel = 'Read' | 'ReadWrite';
export type EnvironmentScope = 'DevTest' | 'Prod';

// Special Global Administrator role
export const GLOBAL_ADMIN_ROLE = 'Global.Administrator';

// Domain-based roles
export const DOMAIN_ROLES = {
  // RFV Domain roles
  RFV_PROD_READ: 'RFV.Prod.Read',
  RFV_PROD_READWRITE: 'RFV.Prod.ReadWrite',
  RFV_DEVTEST_READ: 'RFV.DevTest.Read',
  RFV_DEVTEST_READWRITE: 'RFV.DevTest.ReadWrite',

  // Scheduling Engine Domain roles
  SCHENG_PROD_READ: 'SchEng.Prod.Read',
  SCHENG_PROD_READWRITE: 'SchEng.Prod.ReadWrite',
  SCHENG_DEVTEST_READ: 'SchEng.DevTest.Read',
  SCHENG_DEVTEST_READWRITE: 'SchEng.DevTest.ReadWrite',

  // Global Administrator
  GLOBAL_ADMIN: GLOBAL_ADMIN_ROLE
} as const;

export interface ParsedRole {
  domain: Domain;
  environmentScope?: EnvironmentScope;
  accessLevel?: AccessLevel;
  isGlobalAdmin: boolean;
  originalRole: string;
}

export interface DomainPermissions {
  domain: string;
  canView: boolean;
  canModify: boolean;
  allowedEnvironments: Environment[];
}

export interface PermissionSummary {
  isGlobalAdmin: boolean;
  roles: string[];
  domains: {
    [domainId: string]: DomainPermissions;
  };
}

/**
 * Map domain IDs to role domain names
 */
const DOMAIN_ID_TO_ROLE_MAP: Record<string, string> = {
  'rfv': 'RFV',
  'scheduling-engine': 'SchEng'
};

const ROLE_TO_DOMAIN_ID_MAP: Record<string, string> = {
  'RFV': 'rfv',
  'SchEng': 'scheduling-engine'
};

/**
 * Parse a role string into its components
 */
function parseRole(role: string): ParsedRole | null {
  // Check for Global Administrator
  if (role === GLOBAL_ADMIN_ROLE) {
    return {
      domain: 'Global',
      isGlobalAdmin: true,
      originalRole: role
    };
  }

  // Parse domain-based role: Domain.Environments.AccessLevel
  const parts = role.split('.');
  if (parts.length !== 3) {
    return null;
  }

  const [domain, envScope, access] = parts;

  // Validate domain
  if (!['RFV', 'SchEng'].includes(domain)) {
    return null;
  }

  // Validate environment scope
  if (!['DevTest', 'Prod'].includes(envScope)) {
    return null;
  }

  // Validate access level
  if (!['Read', 'ReadWrite'].includes(access)) {
    return null;
  }

  return {
    domain: domain as Domain,
    environmentScope: envScope as EnvironmentScope,
    accessLevel: access as AccessLevel,
    isGlobalAdmin: false,
    originalRole: role
  };
}

/**
 * Get environments from scope
 */
function getEnvironmentsFromScope(scope: EnvironmentScope): Environment[] {
  if (scope === 'DevTest') {
    return ['DEV', 'TEST'];
  } else if (scope === 'Prod') {
    return ['PROD'];
  }
  return [];
}

/**
 * Check if access level allows modifications
 */
function canModifyWithAccessLevel(accessLevel: AccessLevel): boolean {
  return accessLevel === 'ReadWrite';
}

class PermissionsService {
  /**
   * Parse all user roles
   */
  private parseUserRoles(userRoles: string[]): ParsedRole[] {
    return userRoles
      .map(role => parseRole(role))
      .filter((parsed): parsed is ParsedRole => parsed !== null);
  }

  /**
   * Check if user has Global Administrator role
   */
  isGlobalAdmin(userRoles: string[]): boolean {
    return userRoles.includes(GLOBAL_ADMIN_ROLE);
  }

  /**
   * Check if user has at least one valid role
   */
  hasValidRole(userRoles: string[]): boolean {
    if (this.isGlobalAdmin(userRoles)) {
      return true;
    }
    const validRoles = Object.values(DOMAIN_ROLES);
    return userRoles.some(role => validRoles.includes(role as any));
  }

  /**
   * Get all domains the user has access to
   */
  getUserDomains(userRoles: string[]): string[] {
    if (this.isGlobalAdmin(userRoles)) {
      return ['rfv', 'scheduling-engine'];
    }

    const parsedRoles = this.parseUserRoles(userRoles);
    const domainSet = new Set<string>();

    parsedRoles.forEach(parsed => {
      if (!parsed.isGlobalAdmin && parsed.domain) {
        const domainId = ROLE_TO_DOMAIN_ID_MAP[parsed.domain];
        if (domainId) {
          domainSet.add(domainId);
        }
      }
    });

    return Array.from(domainSet);
  }

  /**
   * Get permissions for a specific domain
   */
  getDomainPermissions(userRoles: string[], domainId: string): DomainPermissions {
    if (this.isGlobalAdmin(userRoles)) {
      return {
        domain: domainId,
        canView: true,
        canModify: true,
        allowedEnvironments: ['DEV', 'TEST', 'PROD']
      };
    }

    const roleDomain = DOMAIN_ID_TO_ROLE_MAP[domainId];
    if (!roleDomain) {
      return {
        domain: domainId,
        canView: false,
        canModify: false,
        allowedEnvironments: []
      };
    }

    const parsedRoles = this.parseUserRoles(userRoles);
    const domainRoles = parsedRoles.filter(
      parsed => !parsed.isGlobalAdmin && parsed.domain === roleDomain
    );

    if (domainRoles.length === 0) {
      return {
        domain: domainId,
        canView: false,
        canModify: false,
        allowedEnvironments: []
      };
    }

    const allowedEnvs = new Set<Environment>();
    let canModify = false;

    domainRoles.forEach(role => {
      if (role.environmentScope) {
        const envs = getEnvironmentsFromScope(role.environmentScope);
        envs.forEach(env => allowedEnvs.add(env));

        if (role.accessLevel && canModifyWithAccessLevel(role.accessLevel)) {
          canModify = true;
        }
      }
    });

    return {
      domain: domainId,
      canView: allowedEnvs.size > 0,
      canModify: canModify,
      allowedEnvironments: Array.from(allowedEnvs)
    };
  }

  /**
   * Check if user can access a domain
   */
  canAccessDomain(userRoles: string[], domainId: string): boolean {
    if (this.isGlobalAdmin(userRoles)) {
      return true;
    }

    const permissions = this.getDomainPermissions(userRoles, domainId);
    return permissions.canView;
  }

  /**
   * Check if user can access a specific environment in a domain
   */
  canAccessEnvironment(userRoles: string[], domainId: string, environment: Environment): boolean {
    if (this.isGlobalAdmin(userRoles)) {
      return true;
    }

    const permissions = this.getDomainPermissions(userRoles, domainId);
    return permissions.allowedEnvironments.includes(environment);
  }

  /**
   * Check if user can modify data in a specific environment within a domain
   */
  canModifyEnvironment(userRoles: string[], domainId: string, environment: Environment): boolean {
    if (this.isGlobalAdmin(userRoles)) {
      return true;
    }

    const roleDomain = DOMAIN_ID_TO_ROLE_MAP[domainId];
    if (!roleDomain) {
      return false;
    }

    const parsedRoles = this.parseUserRoles(userRoles);

    return parsedRoles.some(role => {
      if (role.isGlobalAdmin || role.domain !== roleDomain) {
        return false;
      }

      if (!role.environmentScope || !role.accessLevel) {
        return false;
      }

      const envs = getEnvironmentsFromScope(role.environmentScope);
      if (!envs.includes(environment)) {
        return false;
      }

      return canModifyWithAccessLevel(role.accessLevel);
    });
  }

  /**
   * Check if user can sync between two environments in a domain
   */
  canSyncBetweenEnvironments(
    userRoles: string[],
    domainId: string,
    sourceEnv: Environment,
    targetEnv: Environment
  ): boolean {
    if (this.isGlobalAdmin(userRoles)) {
      return true;
    }

    const canReadSource = this.canAccessEnvironment(userRoles, domainId, sourceEnv);
    const canWriteTarget = this.canModifyEnvironment(userRoles, domainId, targetEnv);

    return canReadSource && canWriteTarget;
  }

  /**
   * Check if user can perform an operation on a domain/environment
   */
  canPerformOperation(
    userRoles: string[],
    domainId: string,
    environment: Environment,
    operation: Operation
  ): boolean {
    if (this.isGlobalAdmin(userRoles)) {
      return true;
    }

    switch (operation) {
      case 'view':
        return this.canAccessEnvironment(userRoles, domainId, environment);
      case 'create':
      case 'update':
      case 'delete':
        return this.canModifyEnvironment(userRoles, domainId, environment);
      case 'sync':
        return this.canModifyEnvironment(userRoles, domainId, environment);
      default:
        return false;
    }
  }

  /**
   * Get user's role display name - shows ALL roles from token
   * Includes both valid domain roles and any other roles in the token
   */
  getUserRoleDisplay(userRoles: string[]): string {
    if (!userRoles || userRoles.length === 0) {
      return 'No Role';
    }

    // Return all roles from the token as a comma-separated list
    return userRoles.join(', ');
  }

  /**
   * Get role description for display
   */
  getRoleDescription(role: string): string {
    if (role === 'Global Administrator') {
      return 'Full access to all domains and environments';
    }

    const parsed = parseRole(role);
    if (!parsed || parsed.isGlobalAdmin) {
      return 'No role assigned';
    }

    const domainName = parsed.domain === 'SchEng' ? 'Scheduling Engine' : parsed.domain;
    const envs = parsed.environmentScope === 'DevTest' ? 'DEV and TEST' : 'PROD';
    const access = parsed.accessLevel === 'ReadWrite' ? 'read and write' : 'read-only';

    return `${domainName} domain - ${access} access to ${envs}`;
  }
}

export const permissionsService = new PermissionsService();
export default permissionsService;
